<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 flex items-center justify-center p-4"
    style="z-index: 70"
  >
    <div
      class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
      @click="handleClose"
    />

    <div
      class="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col fadeIn-0-2s"
      style="height: 90vh; max-height: 900px;"
    >
      <div
        class="p-5 border-b border-gray-100 flex items-center justify-between"
      >
        <h3 class="font-serif font-bold text-xl">
          {{ modalTitle }}
        </h3>
        <button
          @click="handleClose"
          class="flex p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X :size="20" />
        </button>
      </div>

      <div class="p-6 space-y-6 overflow-y-auto flex-1">
        <DeliveryAddressForm
          v-if="shouldShowAddress"
          :address="address"
          :address-error="addressError"
          @update:address="updateAddress"
          @address-input="clearAddressError"
          ref="addressForm"
        />

        <div v-if="diningOption?.description" class="settings-meta">
          <p
            v-html="diningOption.description"
            class="text-sm text-gray-600"
          ></p>
        </div>

        <DeliverySchedulePicker
          v-if="showSchedule"
          :mode="mode"
          @update:mode="handleModeUpdate"
          @update:selectedDay="selectedDay = $event"
          @update:selectedTime="selectedTime = $event"
          :selected-day="selectedDay"
          :selected-time="selectedTime"
          :dining-option="diningOption"
          :store-timezone="getStoreTimezone"
          :store="store"
          :schedule-only="isScheduleOnlyMode"
        />
      </div>

      <div class="p-5 border-t border-gray-100 bg-gray-50">
        <button
          @click="handleSave"
          class="w-full bg-primary-600 text-white font-bold py-4 rounded-xl shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all active:scale-[0.98]"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { X } from "lucide-vue";
import {
  saveOrderConfig,
  loadOrderConfig,
  loadDeliveryAddress,
  saveDeliveryAddress
} from "@/services/localStorageService";

import DeliveryAddressForm from "./DeliveryAddressForm.vue";
import DeliverySchedulePicker from "./DeliverySchedulePicker.vue";
import { useModalManagement } from "@/composables/useModalManagement";
import { useSelectedStore } from "@/composables/useSelectedStore";
import moment from "moment-timezone";

export default {
  name: "DeliverySettingsModal",
  components: {
    X,
    DeliveryAddressForm,
    DeliverySchedulePicker
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    showAddress: {
      type: Boolean,
      default: true
    },
    showSchedule: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      mode: "scheduled", // Default to scheduled, will be adjusted in initializeState
      selectedDay: "Today",
      selectedTime: "",
      address: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zip: ""
      },
      addressError: false
    };
  },
  created() {
    this._modalManagement = useModalManagement(this.$store);
    this._selectedStore = useSelectedStore(this.$store);
  },
  computed: {
    ...mapGetters([
      "dining_option",
      "availableDiningOptions",
      "getStoreTimezone",
      "orderConfig",
      "getDeliveryAddress",
      "storeCountry"
    ]),
    ...mapState(["store"]),
    currentTiming() {
      return this.orderConfig?.timing || "asap";
    },
    currentDay() {
      return this.orderConfig?.scheduledDay || "";
    },
    currentTime() {
      return this.orderConfig?.scheduledTime || "";
    },
    diningOption() {
      if (!this.dining_option || !this.availableDiningOptions) return null;
      return this.availableDiningOptions.find(
        (option) => option.id === this.dining_option
      );
    },
    shouldShowAddress() {
      return this.showAddress && this.diningOption?.is_deliverable === true;
    },

    modalTitle() {
      if (this.shouldShowAddress && !this.showSchedule) {
        return "Set Delivery Address";
      }
      if (!this.shouldShowAddress && this.showSchedule) {
        return "Schedule Time";
      }
      return "Configure Order Settings";
    },

    formattedSelectedDay() {
      if (this.selectedDay === "Today") {
        return "Today";
      }
      if (this.selectedDay === "Tomorrow") {
        return "Tomorrow";
      }
      // Si es una fecha en formato YYYY-MM-DD, formatearla como "Month Day"
      const date = moment(this.selectedDay, "YYYY-MM-DD");
      if (date.isValid()) {
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec"
        ];
        const monthName = monthNames[date.month()];
        const day = date.date();
        return `${monthName} ${day}`;
      }
      return this.selectedDay;
    },
    buttonText() {
      if (this.shouldShowAddress && !this.showSchedule) {
        return "Save Address";
      }
      if (!this.shouldShowAddress && this.showSchedule) {
        // If only schedule orders are allowed, don't show ASAP option
        if (this.isScheduleOnlyMode) {
          return `Schedule for ${this.formattedSelectedDay}`;
        }
        return this.mode === "asap"
          ? "Update to ASAP"
          : `Schedule for ${this.formattedSelectedDay}`;
      }
      // If only schedule orders are allowed, don't show ASAP option
      if (this.isScheduleOnlyMode) {
        return `Schedule for ${this.formattedSelectedDay}`;
      }
      return this.mode === "asap"
        ? "Update to ASAP"
        : `Schedule for ${this.formattedSelectedDay}`;
    },
    /**
     * Checks if the store is in schedule-only mode
     * Returns true if store status is 'schedule_only' or if store is closed but allows scheduling
     */
    isScheduleOnlyMode() {
      if (!this._selectedStore) {
        return false;
      }
      const storeStatus = this._selectedStore.getStoreStatus();
      // A closed or schedule_only store can't take ASAP orders, so only allow
      // scheduled orders (the picker then defaults to the first available time).
      return storeStatus === "schedule_only" || storeStatus === "closed";
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) this.initializeState();
      else this.addressError = false;
    }
  },
  methods: {
    initializeState() {
      if (this.showSchedule) {
        // If schedule-only mode, force mode to 'scheduled'
        if (this.isScheduleOnlyMode) {
          this.mode = "scheduled";
          // Load saved schedule if available
          const savedOrderConfig = loadOrderConfig();
          if (
            savedOrderConfig &&
            savedOrderConfig.scheduledDay &&
            savedOrderConfig.scheduledTime
          ) {
            this.selectedDay = savedOrderConfig.scheduledDay;
            this.selectedTime = savedOrderConfig.scheduledTime;
          } else {
            this.selectedDay = this.currentDay || "Today";
            // Don't set selectedTime here - let DeliverySchedulePicker select the first available
            this.selectedTime = this.currentTime || "";
          }
        } else {
          const savedOrderConfig = loadOrderConfig();

          if (savedOrderConfig && savedOrderConfig.timing) {
            // Only use saved timing if it's not 'asap' when schedule-only mode might be active
            // But since we're in else, we know it's not schedule-only mode
            this.mode = savedOrderConfig.timing;
            this.selectedDay = savedOrderConfig.scheduledDay || "Today";
            // If timing is scheduled but no time is saved, let DeliverySchedulePicker select the first
            if (
              savedOrderConfig.timing === "scheduled" &&
              !savedOrderConfig.scheduledTime
            ) {
              this.selectedTime = "";
            } else {
              this.selectedTime = savedOrderConfig.scheduledTime || "";
            }
            this.$store.commit("SET_ORDER_CONFIG", savedOrderConfig);
          } else {
            // Default to 'asap' only if not in schedule-only mode
            this.mode = this.currentTiming || "asap";
            this.selectedDay = this.currentDay || "Today";
            this.selectedTime = this.currentTime || "";
          }
        }
      }

      if (this.shouldShowAddress) {
        const savedAddress = loadDeliveryAddress();
        const existingAddress = this.$store.getters.getDeliveryAddress;

        if (savedAddress && savedAddress.addressLine1) {
          this.address = savedAddress;
        } else if (existingAddress && existingAddress.address_1) {
          this.address = {
            addressLine1: existingAddress.address_1 || "",
            addressLine2: existingAddress.address_2 || "",
            city: existingAddress.city || "",
            state: existingAddress.state || "",
            zip: existingAddress.postal_code || ""
          };
        } else {
          this.address = {
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zip: ""
          };
        }
      }
    },
    updateAddress(newAddress) {
      this.address = newAddress;
    },
    clearAddressError() {
      this.addressError = false;
    },
    handleModeUpdate(newMode) {
      // If schedule-only mode, prevent switching to ASAP
      if (this.isScheduleOnlyMode && newMode === "asap") {
        return;
      }
      this.mode = newMode;
    },
    handleClose() {
      this._modalManagement.setScheduleModalOpen(false);
    },

    handleSave() {
      if (
        this.shouldShowAddress &&
        (!this.address.addressLine1 || this.address.addressLine1.trim() === "")
      ) {
        this.addressError = true;
        if (this.$refs.addressForm?.focusAddressLine1) {
          this.$refs.addressForm.focusAddressLine1();
        }
        return;
      }

      this.addressError = false;

      if (this.showSchedule) {
        // If schedule-only mode, force timing to 'scheduled'
        const finalMode = this.isScheduleOnlyMode ? "scheduled" : this.mode;
        const newOrderConfig = {
          timing: finalMode,
          scheduledDay:
            finalMode === "scheduled" ? this.selectedDay : undefined,
          scheduledTime:
            finalMode === "scheduled" ? this.selectedTime : undefined
        };
        this.$store.commit("SET_ORDER_CONFIG", newOrderConfig);
        saveOrderConfig(newOrderConfig);
      }

      if (this.shouldShowAddress) {
        const storeCountry = this.storeCountry || "";
        const deliveryAddress = {
          address_1: this.address.addressLine1 || "",
          address_2: this.address.addressLine2 || "",
          city: this.address.city || "",
          state: this.address.state || "",
          postal_code: this.address.zip || "",
          country: storeCountry,
          first_name: "",
          last_name: "",
          phone: "",
          local_id: new Date().toISOString()
        };
        this.$store.dispatch("order/setDeliveryAddress", deliveryAddress);
        saveDeliveryAddress(this.address);
        this.$emit("address-saved", deliveryAddress);
      }
      this._modalManagement.setScheduleModalOpen(false);
    }
  }
};
</script>

<style></style>
